import { useState, useCallback, useEffect, useRef } from 'react'
import { PaginationState } from '@/shared/types'

export function useDebounce<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

export function useToggle(initial = false) {
  const [state, setState] = useState(initial)
  const toggle = useCallback(() => setState(s => !s), [])
  return [state, toggle, setState] as const
}

export function usePagination(initial: PaginationState = { page: 1, per_page: 15 }) {
  const [pagination, setPagination] = useState(initial)
  const goToPage = useCallback((page: number) => setPagination(p => ({ ...p, page })), [])
  const setPerPage = useCallback((per_page: number) => setPagination({ page: 1, per_page }), [])
  return { pagination, setPagination, goToPage, setPerPage }
}

// Drop-in replacement for useState that persists the value in sessionStorage under
// `key`, so it survives a list -> details -> back round trip (a fresh mount would
// otherwise reset it, e.g. list pagination jumping back to page 1). Supports
// functional updates exactly like useState's setter.
export function useSessionState<T>(key: string, initial: T) {
  const storageKey = `list-state:${key}`
  const [state, setStateRaw] = useState<T>(() => {
    try {
      const raw = sessionStorage.getItem(storageKey)
      if (raw != null) return JSON.parse(raw) as T
    } catch {}
    return initial
  })

  const setState = useCallback((value: T | ((prev: T) => T)) => {
    setStateRaw(prev => {
      const next = typeof value === 'function' ? (value as (prev: T) => T)(prev) : value
      try { sessionStorage.setItem(storageKey, JSON.stringify(next)) } catch {}
      return next
    })
  }, [storageKey])

  return [state, setState] as const
}

// Restores the scroll position of the app's main scroll container (#main-scroll-area)
// once `ready` becomes true (e.g. after data has loaded), and keeps saving it as the
// user scrolls so it can be restored when they navigate back to this list.
export function useScrollRestoration(key: string, ready: boolean) {
  const storageKey = `list-scroll:${key}`
  const restored = useRef(false)

  useEffect(() => {
    if (!ready || restored.current) return
    restored.current = true
    const el = document.getElementById('main-scroll-area')
    const saved = sessionStorage.getItem(storageKey)
    if (el && saved) {
      requestAnimationFrame(() => { el.scrollTop = parseInt(saved, 10) || 0 })
    }
  }, [ready, storageKey])

  useEffect(() => {
    const el = document.getElementById('main-scroll-area')
    if (!el) return
    const onScroll = () => sessionStorage.setItem(storageKey, String(el.scrollTop))
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [storageKey])
}

export function useClickOutside<T extends HTMLElement>(handler: () => void) {
  const ref = useRef<T>(null)
  useEffect(() => {
    const listener = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) handler()
    }
    document.addEventListener('mousedown', listener)
    return () => document.removeEventListener('mousedown', listener)
  }, [handler])
  return ref
}
